import {TxtStr} from "/@back@/edit/wed/wedlets/txt/txtTags.js";
import {BOUND_CHAR, BOUND_CHARS} from "/@back@/edit/wed/wedlets/txt/txtUtils.js";
import {ISkSearchAnnot, isSkSearchAnnot} from "/@lib@/edit/schema/schemaSearch.js";
import {ISkAnnot, ISkTextAnnot, isSkTextAnnot} from "/@lib@/edit/schema/schemaAnnots.js";
import {DOM} from "/@lib@/commons/xml/dom.js";
import {ISkDiffAnnot} from "/@lib@/edit/schema/diff.js";
import {WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {HintTextAnnot} from "/@lib@/edit/ot/hints.js";

/**
 * Noeud text DOM dans TxtStr avec ses flags indiquant s'il possède ses BOUND_CHAR en début et/ou en fin.
 */
export interface ITxtStrTextNode extends Text {
	startBoundary: boolean
	endBoundary: boolean
	ime?: ImeComposition
}

export function IS_TxtStrTextNode(n: Node): n is ITxtStrTextNode {return n && n.nodeType === Node.TEXT_NODE}

/**
 * Représente un nbsp dans un TxtStr.
 */
export interface ITxtStrNbSp extends HTMLElement {
}

export function IS_TxtStrNbSp(n: Node): n is ITxtStrNbSp {return n && n.localName === 'x-nbsp'}

/**
 * Enveloppement de ITxtStrTextNode et de ITxtStrNbSp pour l'enrichir d'une annotation : recherche, différentiel...
 *
 * Les élements marks ne s'imbriquent jamais si deux annotations se croisent, la mark est fragmentée, l'arbre est donc applati
 * et l'annotation se constitue de plusieurs marks frères.
 */
export interface ITxtStrMarkNode extends HTMLElement {
	/**
	 * Référence à l'annotation de type de recherche.
	 * les annotations de type recherche ne se croisent jamais.
	 */
	search?: ISkSearchAnnot & ISkTextAnnot

	diff?: ISkDiffAnnot & ISkTextAnnot
}

export function IS_TxtStrMarkNode(n: Node): n is ITxtStrMarkNode {return n?.localName === "mark"}

/** ITxtStrTextNode : Longueur réelle du fragment de texte, une fois les boundary et l'IME en cours éliminées. */
export function xmlLen(txt: ITxtStrTextNode) {
	const l = txt.ime ? txt.length - txt.ime.len : txt.length;
	if (txt.startBoundary) return txt.length - (txt.endBoundary ? 2 : 1);
	return txt.endBoundary ? txt.length - 1 : txt.length;
}

/** ITxtStrTextNode : Offset XML correspondant à un offset web, une fois les boundary et l'IME en cours éliminées */
export function xmlOffset(txt: ITxtStrTextNode, webOffset: number) {
	if (txt.ime?.start < webOffset) webOffset -= txt.ime.len;
	if (txt.startBoundary) webOffset--;
	return webOffset;
}

/** ITxtStrTextNode : Offset web correspondant à un offset xml, une fois les boundary et l'IME en cours pris en compte */
export function webOffset(txt: ITxtStrTextNode, xmlOffset: number) {
	if (txt.startBoundary) xmlOffset++;
	if (txt.ime?.start < xmlOffset) xmlOffset += txt.ime.len;
	return xmlOffset;
}

/** ITxtStrTextNode : Valeur XML de ce noeud texte, une fois les boundary et l'IME éliminées */
export function xmlText(txt: ITxtStrTextNode): string {
	if (txt.ime?.len > 0) {
		const first = txt.nodeValue.substring(txt.startBoundary ? 1 : 0, txt.ime.start);
		const last = txt.nodeValue.substring(txt.ime.start + txt.ime.len, txt.endBoundary ? txt.nodeValue.length - 1 : txt.nodeValue.length);
		return first + last;
	}
	if (txt.startBoundary) return txt.nodeValue.substring(1, txt.endBoundary ? txt.nodeValue.length - 1 : txt.nodeValue.length);
	if (txt.endBoundary) return txt.nodeValue.substring(0, txt.nodeValue.length - 1);
	return txt.nodeValue;
}


/** Xml offset d'un point dans un TxtStr. */
export function getXmlOffset(txtStr: TxtStr, from: Node, webOffset: number): number {
	let result = 0;
	if (IS_TxtStrTextNode(from)) {
		result = xmlOffset(from, webOffset);
	} else if (IS_TxtStrNbSp(from)) {
		result = 1;
	} else {
		const ch = from.childNodes[webOffset];
		from = ch || from.lastChild || from;
		if (from === txtStr) return 0;
		if (IS_TxtStrTextNode(from)) result = xmlLen(from);
		else if (IS_TxtStrNbSp(from)) result = 1;
	}
	do {
		const parent = from.parentNode;
		while (from = from.previousSibling) {
			if (IS_TxtStrTextNode(from)) result += xmlLen(from);
			else if (IS_TxtStrNbSp(from)) result += 1;
			else if (IS_TxtStrMarkNode(from)) result += markLen(from);
		}
		if (parent !== txtStr) from = parent;
	} while (from);
	return result;
}

export function getWebOffset(txtStr: TxtStr, xmlOffset: number): [ITxtStrTextNode, number] {
	const result: [ITxtStrTextNode, number] = [] as any;
	DOM.findNext(txtStr, txtStr, (n: Node): n is Node => {
		if (IS_TxtStrTextNode(n)) {
			const nodeLen = xmlLen(n);
			if (xmlOffset <= nodeLen) {
				result.push(n, webOffset(n, xmlOffset));
				return true;
			}
			xmlOffset -= nodeLen;
		} else if (IS_TxtStrNbSp(n)) {
			if (xmlOffset === 0) console.log("texNode missing before nbsp", txtStr);
			xmlOffset--;
		} else if (IS_TxtStrMarkNode(n)) {
			for (let ch = n.firstChild; ch; ch = ch.nextSibling) {
				if (IS_TxtStrTextNode(ch)) {
					const len = xmlLen(ch);
					if (xmlOffset <= len) {
						result.push(ch, webOffset(ch, xmlOffset));
						return true;
					}
				} else if (IS_TxtStrNbSp(n)) {
					xmlOffset--;
				} //else if (IS_TxtStrMarkNode(from)) pas de marks dans des marks
			}
		}
		return false;
	});
	return result; //[this.firstChild, xmlOffset + 1];
}

/**
 * Insert une chaine de caractère dans ce contexte.
 * Au premier appel si 0 n'est pas retourné, le point d'insertion n'a pas été trouvé, texte non inséré.
 */
export function insertXmlText(txtStr: TxtStr, node: Node, offset: number, xmlText: string): number {
	if (IS_TxtStrTextNode(node)) {
		const len = xmlLen(node);
		if (len >= offset) {
			insertHtmlTextHere(txtStr, node, offset, xmlText);
			return 0;
		} else {
			offset -= len;
		}
	} else if (IS_TxtStrNbSp(node)) {
		if (offset === 0) {
			const txt = node.parentNode.insertBefore(newTxtStrTextNode(), node);
			insertHtmlTextHere(txtStr, txt, 0, xmlText);
			return 0;
		} else if (offset === 1) {
			let next = node.nextSibling;
			if (!IS_TxtStrTextNode(next)) next = node.parentNode.insertBefore(newTxtStrTextNode(), next);
			insertHtmlTextHere(txtStr, next as ITxtStrTextNode, 0, xmlText);
			return 0;
		} else {
			return offset - 1;
		}
	} else {
		// Root TxtStr ou Mark
		for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
			offset = insertXmlText(txtStr, ch, offset, xmlText);
			if (offset === 0) return 0;
		}
	}
	if (offset === 0) {
		//pas trouvé, mais on a atteint le point d'insertion
		const txt = node.appendChild(newTxtStrTextNode());
		insertHtmlTextHere(txtStr, txt, 0, xmlText);
		return 0;
	}
	return offset;
}

/** Insert une chaine de caractère dans ce noeud texte. */
function insertHtmlTextHere(txtStr: TxtStr, txtNode: ITxtStrTextNode, xmlOffset: number, xmlText: string) {
	let start = 0;
	let end = xmlText.indexOf('\u00A0');
	while (end >= 0) {
		//fragmentation de txtNode pour les espaces insécables.
		const insOffset = webOffset(txtNode, xmlOffset);
		const lenToRemove = xmlLen(txtNode) - xmlOffset;
		const moveStr = lenToRemove > 0 ? txtNode.nodeValue.substr(insOffset, lenToRemove) : null;
		if (start < end) txtNode.replaceData(insOffset, lenToRemove, xmlText.substring(start, end));
		else if (lenToRemove > 0) txtNode.deleteData(insOffset, lenToRemove);
		const nbsp = txtNode.parentNode.insertBefore(document.createElement('x-nbsp'), txtNode.nextSibling);
		txtNode = nbsp.nextSibling as any;
		if (!IS_TxtStrTextNode(txtNode)) txtNode = nbsp.parentNode.insertBefore(newTxtStrTextNode(), txtNode);
		if (moveStr) txtNode.insertData(1, moveStr);
		xmlOffset = 0;
		start = end + 1;
		end = xmlText.indexOf('\u00A0', start);
	}
	if (start < xmlText.length) txtNode.insertData(webOffset(txtNode, xmlOffset), xmlText.substring(start));
}

export function deleteXmlText(txtStr: TxtStr, node: Node, point: { start: number, end: number }): ChildNode {
	if (IS_TxtStrTextNode(node)) {
		const len = xmlLen(node);
		if (len > point.start) {
			//on supprime du contenu ici
			const webStart = webOffset(node, point.start);
			if (len >= point.end) {
				//fini
				node.deleteData(webStart, webOffset(node, point.end) - webStart);
				point.start = point.end;
				return null;
			}
			node.deleteData(webStart, webOffset(node, len) - webStart);
			point.end -= len;
			point.start = 0;
		} else {
			//la suppr a lieu après.
			point.start -= len;
			point.end -= len;
		}
	} else if (IS_TxtStrNbSp(node)) {
		if (point.start === 0) {
			//on suppr ce nbsp
			const prev = node.previousSibling;
			let next = node.nextSibling;
			node.remove();
			point.end--;
			if (IS_TxtStrTextNode(prev)) {
				while (IS_TxtStrTextNode(next)) {
					//Si prev et next sont des noeuds texte consécutifs on doit n'en garder qu'un
					const lenNext = xmlLen(next);
					if (lenNext <= point.end) {
						//node suivant est entièrement supprimé
						if (prev.endBoundary) {
							if (!next.endBoundary) {
								prev.deleteData(prev.length - 1, 1);
								prev.endBoundary = false;
							}
						} else if (next.endBoundary) {
							prev.insertData(prev.length, BOUND_CHAR);
							prev.endBoundary = true;
						}
						next.remove();
						point.end -= lenNext;
						if (point.end === 0) return null;
						next = prev.nextSibling;
					} else {
						//on fusionne les 2 noeuds texte.
						const lenPrev = xmlLen(prev);
						if ((prev.startBoundary || false) == (next.startBoundary || false)) {
							if (lenPrev > 0) next.insertData(next.startBoundary ? 1 : 0, xmlText(prev));
						} else {
							if (next.startBoundary) next.deleteData(0, 1);
							next.insertData(0, prev.endBoundary ? prev.nodeValue.substring(0, prev.nodeValue.length - 1) : prev.nodeValue);
							next.startBoundary = prev.startBoundary;
						}
						if (lenPrev > 0) {
							point.start += lenPrev;
							point.end += lenPrev;
						}
						const selMgr = txtStr.txtRoot.selMgr;
						if (selMgr.type === 'Caret' && selMgr.focusNode === prev) selMgr.collapse(next, selMgr.focusOffset);
						prev.remove();
						break;
					}
				}
			}
			if (point.end === 0) return null;
			return next;
		} else {
			//la suppr a lieu après.
			point.start--;
			point.end--;
		}
	} else {
		// Mark ou Txtstr racine, on s'enfonce
		for (let ch = node.firstChild; ch;) {
			ch = deleteXmlText(txtStr, ch, point);
		}
	}
	return point.start === point.end ? null : node?.nextSibling;
}

/** Cleanup un TxtStr complètement.*/
export function resetXmlText(this: void, txtStr: TxtStr): Node {
	txtStr.textContent = null;
	return txtStr.appendChild(newTxtStrTextNode());
}


export function planRedrawTxtStrAnnots(txtStr: TxtStr) {
	if (!txtStr.redrawAnnotRequest) txtStr.redrawAnnotRequest = window.requestIdleCallback(txtStr.redrawAnnotExecutor || (txtStr.redrawAnnotExecutor = redrawAnnotExecutor.bind(txtStr)))
}

function redrawAnnotExecutor(this: TxtStr, deadline: RequestIdleDeadline) {
	this.redrawAnnotRequest = 0;
	if (!this.isConnected) return;
	//TODO GET SEL
	const selMgr = this.txtRoot.selMgrAsIs;
	let anchorOffset = -1;
	let focusOffset = -1;
	if (DOM.isAncestor(this, selMgr.anchorNode)) {
		anchorOffset = this.getXmlOffset(selMgr.anchorNode, selMgr.anchorOffset);
	}
	if (DOM.isAncestor(this, selMgr.focusNode)) {
		focusOffset = this.getXmlOffset(selMgr.focusNode, selMgr.focusOffset);
	}
	const newAnnots = this.wedMgr.docHolder.getAnnots(this.wedAnchor);
	const toKeep = new Set<ISkAnnot>();
	let offset = 0;
	//parcours pour remplacer les anciennes annots par les nouvelles
	for (let ch = this.firstChild; ch; ch = ch.nextSibling) {
		if (IS_TxtStrTextNode(ch)) {
			offset += xmlLen(ch);
		} else if (IS_TxtStrNbSp(ch)) {
			offset += 1;
		} else if (IS_TxtStrMarkNode(ch)) {
			const markLenght = markLen(ch);
			for (let i = 0; i < newAnnots.length; i++) {
				const a = newAnnots[i];
				if (a && isMatchNewAnnot(ch, offset, markLenght, a)) {
					newAnnots[i] = null;
					toKeep.add(a);
				}
			}
			offset += markLenght;
		}
	}

	//parcours pour supprimer toutes les annots sauf celles qui sont dans toKeep
	removeAnnotsInTxtStr(this, toKeep);

	//ajout des newAnnots restantes.
	for (const a of newAnnots) {
		if (isSkTextAnnot(a) && !(a instanceof HintTextAnnot)) injectAnnotInTxtStr(this, a.offsetStart, a);
	}

	//restore sel
	if (anchorOffset >= 0) {
		const [ancN, ancOffs] = this.getWebOffset(anchorOffset);
		if (focusOffset >= 0) {
			const [focN, focOffs] = this.getWebOffset(focusOffset);
			selMgr.setSelBounds(ancN, ancOffs, focN, focOffs);
		} else {
			selMgr.setSelAnchor(ancN, ancOffs);
		}
	} else if (focusOffset >= 0) {
		const [focN, focOffs] = this.getWebOffset(focusOffset);
		selMgr.setSelFocus(focN, focOffs);
	}
	//replace hints
	if (this.hints) for (const [h, r] of this.hints) this.placeHint(h, r);
}


/**
 * Injecte une annotation de caractère dans ce contexte.
 */
export function injectAnnotInTxtStr(txtStr: TxtStr, pos: number, annot: ISkTextAnnot) {
	let node = txtStr.firstChild;
	while (node) {
		if (IS_TxtStrTextNode(node)) {
			let len = xmlLen(node);
			if (len > pos) {
				const annotLen = annot.len;
				let textNode: ITxtStrTextNode;
				if (pos === 0) {
					textNode = node;
				} else {
					textNode = splitTextForMark(node, pos);
					len -= pos;
				}
				if (len > annotLen) {
					splitTextForMark(textNode, annotLen);
					len = annotLen;
				}
				const mark = txtStr.insertBefore(newTxtStrMarkNode(annot), textNode);
				mark.appendChild(textNode);
				extendMarkUntil(txtStr, mark, annot, annotLen - len);
				return;
			} else {
				pos -= len;
			}
		} else if (IS_TxtStrNbSp(node)) {
			if (pos === 0) {
				const mark = txtStr.insertBefore(newTxtStrMarkNode(annot), node);
				mark.appendChild(node);
				extendMarkUntil(txtStr, mark, annot, annot.len - 1);
				return;
			} else {
				pos--;
			}
		} else if (IS_TxtStrMarkNode(node)) {
			// Mark
			let len = markLen(node);
			if (pos < len) {
				//début de mark dans cette mark
				const annotLen = annot.len;
				let mark: ITxtStrMarkNode;
				if (pos === 0) {
					//1ère mark trouvée
					mark = node;
				} else {
					//Début au milieu de la mark, on la split.
					mark = splitMark(node, pos);
					len -= pos;
				}
				if (annotLen < len) {
					//Fin au milieu de la mark, on la split.
					splitMark(mark, annotLen);
					len = annotLen;
				}
				addAnnotTo(mark, annot);
				extendMarkUntil(txtStr, mark, annot, annotLen - len);
				return;
			} else {
				//On saute intégralement cette mark
				pos -= len;
			}
		}
		node = node.nextSibling;
	}
	return;
}


function extendMarkUntil(txtStr: TxtStr, mark: ITxtStrMarkNode, annot: ISkTextAnnot, lenLeft: number) {
	//prev est le noeud courant, qui peut être la marque à étendre ou un autre noeud
	// qu'il ne faut alors pas étendre, mais recréer une nouvelle mark.
	let prev = mark;

	function addMarkAround(node: Node) {
		if (prev !== mark) {
			//prev n'est pas une mark à étendre
			const newMark = newTxtStrMarkNode(annot);
			node.parentNode.insertBefore(newMark, node);
			newMark.appendChild(node);
			mark = prev = newMark; //nouvelle mark à étendre
		} else {
			mark.appendChild(node);
		}
	}

	while (lenLeft > 0) {
		const node = prev.nextSibling;
		if (IS_TxtStrTextNode(node)) {
			const nodeLen = xmlLen(node);
			if (lenLeft < nodeLen) {
				splitTextForMark(node, lenLeft);
				addMarkAround(node);
				return;
			}
			addMarkAround(node);
			lenLeft -= nodeLen;
		} else if (IS_TxtStrNbSp(node)) {
			addMarkAround(node);
			lenLeft--;
		} else if (IS_TxtStrMarkNode(node)) {
			const lenMark = markLen(node);
			if (lenLeft < lenMark) {
				//Fin de l'annot au milieu de cette mark
				splitMark(node, lenLeft);
				addAnnotTo(node, annot);
				return;
			}
			addAnnotTo(node, annot);
			prev = node; //on saute cette marque différente.
			lenLeft -= lenMark;
		} else if (node) {
			//autre node ?
			addMarkAround(node);
			//lenLeft--; non, node ne représente pas un caractère.
		} else {
			ERROR.log(`node null, but lenLeft=${lenLeft} in txtStr: ${txtStr.outerHTML}\nxa: ${JSON.stringify(txtStr.wedlet.wedAnchor)}\napp: ${document.location.hash}`, Error());
			lenLeft = 0;
		}
	}
}

export function removeAnnotsInTxtStr(txtStr: TxtStr, exceptFor: Set<ISkAnnot>) {
	let ch = txtStr.firstChild;
	while (ch) {
		if (IS_TxtStrMarkNode(ch)) {
			if (remAllAnnotsExcept(ch, exceptFor)) {
				//Unwrap
				const prev = ch.previousSibling;
				const next = ch.nextSibling;
				const firstCh = ch.firstChild;
				const lastCh = ch.lastChild;
				if (IS_TxtStrTextNode(prev) && IS_TxtStrTextNode(firstCh)) {
					//Deux noeuds texte à merger au début.
					prev.appendData(firstCh.nodeValue);
					prev.endBoundary = firstCh.endBoundary;
					firstCh.remove();
					if (firstCh === lastCh) {
						//Cas d'un seul noeud text dans la mark
						if (IS_TxtStrTextNode(next)) {
							//fusion text avec le next de la mark.
							prev.appendData(next.nodeValue);
							prev.endBoundary = next.endBoundary;
							next.remove();
						}
						//Suppr mark
						const m = ch.nextSibling;
						ch.remove();
						ch = m;
						continue;
					}
				}
				if (IS_TxtStrTextNode(next) && IS_TxtStrTextNode(lastCh)) {
					//Deux noeuds texte à merger à la fin.
					next.insertData(0, lastCh.nodeValue);
					next.startBoundary = lastCh.startBoundary;
					lastCh.remove();
				}
				while (ch.hasChildNodes()) txtStr.insertBefore(ch.firstChild, ch);
				const m = ch.nextSibling as ChildNode;
				ch.remove();
				ch = m;
				continue;
			}
		}
		ch = ch.nextSibling;
	}
}

//
// export function removeAnnotInTxtStr(txtStr: TxtStr, annot: ISkTextAnnot) {
// 	let ch = txtStr.firstChild;
// 	while (ch) {
// 		if (IS_TxtStrMarkNode(ch) && isMarkRefAnnot(ch, annot)) {
// 			if (remAnnotTo(ch, annot)) {
// 				//Unwrap
// 				const prev = ch.previousSibling;
// 				const next = ch.nextSibling;
// 				const firstCh = ch.firstChild;
// 				const lastCh = ch.lastChild;
// 				if (IS_TxtStrTextNode(prev) && IS_TxtStrTextNode(firstCh)) {
// 					//Deux noeuds texte à merger au début.
// 					prev.appendData(firstCh.nodeValue);
// 					prev.endBoundary = firstCh.endBoundary;
// 					firstCh.remove();
// 					if (firstCh === lastCh) {
// 						//Cas d'un seul noeud text dans la mark
// 						if (IS_TxtStrTextNode(next)) {
// 							//fusion text avec le next de la mark.
// 							prev.appendData(next.nodeValue);
// 							prev.endBoundary = next.endBoundary;
// 							next.remove();
// 						}
// 						//Suppr mark
// 						const m = ch.nextSibling;
// 						ch.remove();
// 						ch = m;
// 						continue;
// 					}
// 				}
// 				if (IS_TxtStrTextNode(next) && IS_TxtStrTextNode(lastCh)) {
// 					//Deux noeuds texte à merger à la fin.
// 					next.insertData(0, lastCh.nodeValue);
// 					next.startBoundary = lastCh.startBoundary;
// 					lastCh.remove();
// 				}
// 				while (ch.hasChildNodes()) txtStr.insertBefore(ch.firstChild, ch);
// 				const m = ch.nextSibling as ChildNode;
// 				ch.remove();
// 				ch = m;
// 				continue;
// 			}
// 		}
// 		ch = ch.nextSibling;
// 	}
// }

/** Création d'un noeud text par défaut avec ses bounds. */
function newTxtStrTextNode(): ITxtStrTextNode {
	const t = document.createTextNode(BOUND_CHARS) as ITxtStrTextNode;
	t.startBoundary = true;
	t.endBoundary = true;
	return t;
}

/** Création d'une mark associée à son annot. */
function newTxtStrMarkNode(annot: ISkTextAnnot): ITxtStrMarkNode {
	return addAnnotTo(document.createElement("mark") as ITxtStrMarkNode, annot);
}

function splitTextForMark(node: ITxtStrTextNode, pos: number): ITxtStrTextNode {
	const nextT = node.splitText(webOffset(node, pos)) as ITxtStrTextNode;
	nextT.startBoundary = false;
	nextT.endBoundary = node.endBoundary;
	node.endBoundary = false;
	return nextT;
}

/** Segmente une mark à une position donnée en recopiant ses associations aux annots. */
function splitMark(mark: ITxtStrMarkNode, pos: number): ITxtStrMarkNode {
	const nextMark = document.createElement("mark") as ITxtStrMarkNode;
	if (mark.search) addAnnotTo(nextMark, mark.search);
	if (mark.diff) addAnnotTo(nextMark, mark.diff);
	mark.parentNode.insertBefore(nextMark, mark.nextSibling);
	//Recherche du 1er déplacement
	let node = mark.firstChild;
	//Recherche du 1er elt à déplacer dans nextMark.
	while (node && pos > 0) {
		if (IS_TxtStrTextNode(node)) {
			const len = xmlLen(node);
			if (pos < len) splitTextForMark(node, pos);
			pos -= len;
		} else if (IS_TxtStrNbSp(node)) {
			pos--;
		}
		node = node.nextSibling;
	}
	//Déplacement de ce 1er et des suivants.
	while (node) {
		const n = node.nextSibling;
		nextMark.appendChild(node);
		node = n;
	}
	return nextMark;
}

function addAnnotTo(mark: ITxtStrMarkNode, annot: ISkTextAnnot): ITxtStrMarkNode {
	if (isSkSearchAnnot(annot)) {
		mark.search = annot;
	} else if (WEDLET.diffLib?.isDiffAnnot(annot)) {
		mark.diff = annot;
	} else {
		console.trace("Unknown annot", annot);
	}
	mark.classList.add(annot.level.name);
	return mark;
}

/**
 * @return true si cette mark n'a plus de ref et devrait être éliminée.
 */
function remAllAnnotsExcept(mark: ITxtStrMarkNode, annots: Set<ISkAnnot>): boolean {
	let discard = true;
	if (mark.search) {
		if (annots.has(mark.search)) {
			discard = false;
		} else {
			mark.classList.remove(mark.search.level.name);
			mark.search = null;
		}
	}
	if (mark.diff) {
		if (annots.has(mark.diff)) {
			discard = false;
		} else {
			mark.classList.remove(mark.diff.level.name);
			mark.diff = null;
		}
	}
	return discard;
}

// /**
//  * @return true si cette mark n'a plus de ref et devrait être éliminée.
//  */
// function remAnnotTo(mark: ITxtStrMarkNode, annot: ISkTextAnnot): boolean {
// 	if (mark.search === annot) {
// 		mark.search = null;
// 	} else {
// 		// TODO diff
// 		throw Error("Annot type unknown...");
// 	}
// 	return true;
// }

// function isMarkRefAnnot(mark: ITxtStrMarkNode, annot: ISkTextAnnot): boolean {
// 	if (annot.type === SkSearchAnnot.TYPE) return mark.search === annot;
// 	//TODO diff
// }

/**
 * Return true si cette annot est associée à cette mark (diff) ou peut-être réassociée à cette mark (search).
 * @param markOffset offset XML du début de mark.
 */
function isMatchNewAnnot(mark: ITxtStrMarkNode, markOffset: number, markLength: number, newAnnot: ISkAnnot): boolean {
	if (mark.search) {
		if (mark.search.search === (newAnnot as ISkSearchAnnot).search && isSkTextAnnot(newAnnot)) {
			if (newAnnot.offsetStart === markOffset && newAnnot.len === markLength) {
				//on ne gère que le cas simple, les cas complexes avec croisement d'autres marks seront reconstruits
				//on switch vers cette nouvelle annot.
				mark.search = newAnnot as ISkSearchAnnot & ISkTextAnnot;
				return true;
			}
		}
	}
	if (mark.diff === newAnnot) {
		return true;
	}
	return false;
}

function markLen(mark: ITxtStrMarkNode): number {
	let l = 0;
	for (let ch = mark.firstChild; ch; ch = ch.nextSibling) {
		if (IS_TxtStrTextNode(ch)) l += xmlLen(ch);
		else if (IS_TxtStrNbSp(ch)) l += 1;
		else if (IS_TxtStrMarkNode(ch)) l += markLen(ch);
	}
	return l;
}

/**
 * Représente une IME composition en cours d'édition.
 * Le noeud texte conerné est donc désynchonisé par rapport à l'état reel de l'éditeur.
 * A partir l'offset start, len caratctères sont issus de cette composition et non encore
 * sync avec l'éditeur.
 * Cet objet ImeComposition est associé au noeud texte correspondant.
 */
export class ImeComposition {
	start: number;
	len: number;
}